import React, { useCallback } from 'react';
import { noop } from 'lodash';

import IP from 'constants/form/inputParser.const';
import useInputHelpers from 'hooks/useInputHelpers.hook';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import canonicalizeSelectOptions from 'utils/react/canonicalizeSelectOptions.util';
import createNodeKey from 'utils/react/createNodeKey.util';
import asInput from 'utils/strings/asInput.util';

import CN from './EnumeratedInput.module.scss';

const CTAG = 'EnumeratedInput';

const EnumeratedInput = ({
  children, // removed from extra, so it is not passed to the <input>
  name,
  onBlur = noop,
  onChange = noop,
  onClick = noop,
  onFocus = noop,
  options: optionsFromProps,
  placeholder: placeholderFromProps,
  value: valueFromProps,
  tag: tagFromProps,
  ...extra
}) => {
  const { attributes, value, focus, handle } = useInputHelpers(IP.text, valueFromProps, name, tagFromProps);
  const { received: receivedFocus, lost: lostFocus } = focus;

  const handleChange = useCallback((ev) => handle(`${CTAG}.onChange`, onChange, ev), [handle, onChange]);
  const handleClick = useCallback((ev) => handle(`${CTAG}.onClick`, onClick, ev), [handle, onClick]);

  const handleFocus = useCallback(
    (ev) => {
      receivedFocus();
      return handle(`${CTAG}.onFocus`, onFocus, ev);
    },
    [receivedFocus, handle, onFocus],
  );

  const handleBlur = useCallback(
    (ev) => {
      lostFocus();
      return handle(`${CTAG}.onBlur`, onBlur, ev);
    },
    [handle, lostFocus, onBlur],
  );

  // transform whatever comes from outside into an array of objects which shape we know of
  const options = canonicalizeSelectOptions(optionsFromProps);
  const placeholder = fromAnyToTrimmed(placeholderFromProps);
  const isPlaceholderRendered = fromAnyToBool(placeholder && !fromAnyToTrimmed(value));

  // NOTE: for styling the placeholder text
  // SEE: https://stackoverflow.com/questions/15602140/how-to-style-disabled-options-in-a-form

  return (
    <div data-bem="EnumeratedInput" tabIndex={-1} onBlur={handleBlur} onFocus={handleFocus} className={CN.component}>
      <select
        autoComplete="off"
        {...extra}
        {...attributes}
        data-bem="EnumeratedInput__select"
        className={CN.input}
        value={asInput(value)}
        onChange={handleChange}
      >
        {isPlaceholderRendered && (
          <option value="" disabled selected>
            {placeholder}
          </option>
        )}

        {options.map((option, index) => {
          const { value, description } = option;
          const key = createNodeKey({ index, tag: value });

          return (
            <option
              key={key}
              data-bem="EnumeratedInput__option"
              role="menuitem"
              tabIndex="0"
              value={value}
              onClick={handleClick}
            >
              {description}
            </option>
          );
        })}
      </select>
    </div>
  );
};

export default EnumeratedInput;
